(function() {
  'use strict';
  window.Brume = window.Brume || {};

/**
 * Buy Page Gallery
 * Desktop: static 2-column image mosaic (all tiles shown via CSS).
 * Mobile:  infinite swipe carousel, with dots.
 * Both:    react to the colour selector via Brume.switchColor().
 */

let currentColor = 'silver';

// desktop gallery refs
let slideshow, track;
// mobile gallery refs
let bgm, bgmTrack, bgmImage, bgmDots;

/* Infinite loop.
   The track holds THREE copies of the slide list: [copy][real][copy]. The user
   always sits in the middle copy, so there is real content to swipe into in both
   directions, endlessly. Whenever the scroll position leaves the middle copy we
   shift it back by exactly one copy-width. Every copy is pixel-identical, so the
   shift is invisible and the swipe simply keeps going.
   A single trailing clone (the obvious approach) does not work: you reach its
   edge and the track dead-ends. */
let bgmRealCount = 0;    // slides in ONE copy
let bgmStep = 0;         // px from one slide's left edge to the next
let bgmSettleTimer = null; // fires the wrap once scrolling stops
let bgmHasLead = false;  // leading copy added? (withheld until first forward move)
let bgmLastReportedIndex = 0; // last slide reported to analytics (starts on slide 1)

function isMobile() {
  return window.matchMedia('(max-width: 768px)').matches;
}

/* ─────────── desktop: static mosaic ───────────
   The desktop gallery is a 2-column image mosaic (all tiles shown via CSS).
   No carousel: nothing to wire up here. */
function initDesktop() {}

/* ─────────── mobile: infinite carousel ─────────── */
function bgmSlides() {
  return bgmTrack ? Array.from(bgmTrack.querySelectorAll('.bgm-slide')) : [];
}

// Build one throwaway copy of a real slide.
function bgmCloneSlide(slide) {
  const clone = slide.cloneNode(true);
  clone.classList.add('bgm-clone');
  clone.setAttribute('aria-hidden', 'true');
  // Duplicate ids would break getElementById lookups (e.g. #bgmImage).
  clone.removeAttribute('id');
  clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  // Controls in a clone would act on the wrong element.
  clone.querySelectorAll('button').forEach((b) => b.remove());
  // Swap cloned videos for their poster image: three copies of every video
  // would mean several decoders running at once for no visual gain.
  clone.querySelectorAll('video').forEach((v) => {
    const img = document.createElement('img');
    img.className = v.className;
    img.src = v.getAttribute('poster') || '';
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.loading = 'lazy';
    img.decoding = 'async';
    v.parentNode.replaceChild(img, v);
  });
  return clone;
}

function bgmMeasureStep() {
  const slides = bgmSlides();
  if (slides.length < 2) return 0;
  return slides[1].offsetLeft - slides[0].offsetLeft;
}

function bgmCopyWidth() {
  return bgmRealCount * bgmStep;
}

// Scroll offset where the real slides begin. Zero until the leading copy is
// added, which is what makes the first slide a hard left edge on arrival.
function bgmHomeOffset() {
  return bgmHasLead ? bgmCopyWidth() : 0;
}

// Park the viewport on the first real slide.
function bgmResetToHome() {
  if (!bgmTrack || !bgmStep) return;
  bgmTrack.scrollLeft = bgmHomeOffset();
}

/* The leading copy is deliberately withheld at first. With nothing to the left
   of slide 1, the browser's own scroll boundary stops a backward swipe — which
   is exactly the "you can't scroll left on arrival" behaviour, achieved
   natively rather than by fighting the gesture. It gets added the moment the
   visitor moves forward, so the loop becomes fully bidirectional from then on. */
function bgmAddLeadCopy() {
  if (bgmHasLead || !bgmTrack || !bgmRealCount) return;
  const real = bgmSlides().slice(0, bgmRealCount);
  const frag = document.createDocumentFragment();
  real.forEach((s) => frag.appendChild(bgmCloneSlide(s)));
  const x = bgmTrack.scrollLeft;
  bgmTrack.insertBefore(frag, bgmTrack.firstChild);
  bgmHasLead = true;
  // Compensate in the same task so no frame paints at the shifted position.
  bgmTrack.scrollLeft = x + bgmCopyWidth();
}

function bgmBuildLoop() {
  if (!bgmTrack) return;
  const real = bgmSlides();
  if (real.length < 2) return;
  bgmRealCount = real.length;

  // Trailing copy only — see bgmAddLeadCopy for why the leading one waits.
  const after = document.createDocumentFragment();
  real.forEach((s) => after.appendChild(bgmCloneSlide(s)));
  bgmTrack.appendChild(after);

  bgmStep = bgmMeasureStep();
  bgmResetToHome();
  // Slide widths are percentage-based so the step is known before images load,
  // but re-measure once after paint in case fonts/images shift the layout.
  window.requestAnimationFrame(() => {
    const s = bgmMeasureStep();
    if (s && Math.abs(s - bgmStep) > 1) {
      bgmStep = s;
      if (!bgmHasLead) bgmResetToHome();
    }
  });
}

function bgmBuildDots() {
  if (!bgmDots) return;
  bgmDots.innerHTML = '';
  const count = bgmRealCount || bgmSlides().length;
  if (count <= 1) return;
  for (let i = 0; i < count; i++) {
    const dot = document.createElement('button');
    dot.className = 'bgm-dot' + (i === 0 ? ' active' : '');
    dot.setAttribute('aria-label', 'Go to item ' + (i + 1));
    dot.addEventListener('click', ((n) => () => bgmScrollTo(n))(i));
    bgmDots.appendChild(dot);
  }
}

function bgmSetDot(index) {
  if (!bgmDots) return;
  bgmDots.querySelectorAll('.bgm-dot').forEach((d, i) => {
    d.classList.toggle('active', i === index);
  });
}

// Absolute slide index under the current scroll position.
function bgmAbsIndex() {
  if (!bgmTrack || !bgmStep) return 0;
  const slides = bgmSlides();
  let idx = Math.round(bgmTrack.scrollLeft / bgmStep);
  if (idx < 0) idx = 0;
  if (idx > slides.length - 1) idx = slides.length - 1;
  return idx;
}

// Which of the REAL slides is showing, regardless of which copy we're in.
function bgmRealIndex() {
  if (!bgmRealCount) return 0;
  return ((bgmAbsIndex() % bgmRealCount) + bgmRealCount) % bgmRealCount;
}

// Scroll to a real slide, targeting the real (home) copy.
function bgmScrollTo(realIndex) {
  if (!bgmTrack || !bgmStep) return;
  const left = bgmHomeOffset() + realIndex * bgmStep;
  bgmTrack.scrollTo({ left: left, behavior: 'smooth' });
  // Some engines silently ignore `behavior: 'smooth'` on a scroll-snap
  // container. If nothing moved, jump instead so the tap/dot still works.
  window.setTimeout(function () {
    if (bgmTrack && Math.abs(bgmTrack.scrollLeft - left) > 2) {
      bgmTrack.scrollTo({ left: left, behavior: 'auto' });
    }
  }, 400);
}

/* Shift the viewport back onto the real copy.

   This must NOT run while the user is still scrolling. The track is
   `scroll-snap-type: x mandatory`, which snaps every programmatic scrollLeft
   assignment to the nearest slide — so wrapping mid-gesture yanks the strip out
   from under the finger and reads as a stutter. It only runs once scrolling has
   settled. That is safe because there is a whole copy (all slides) of runway
   beyond the real set: no single gesture travels far enough to reach a track
   edge before the wrap fires. */
function bgmWrap() {
  if (!bgmTrack) return;
  const w = bgmCopyWidth();
  if (!w) return;
  const home = bgmHomeOffset();
  const x = bgmTrack.scrollLeft;
  if (x >= home && x < home + w) return; // already on the real copy
  bgmTrack.scrollLeft = x >= home + w ? x - w : x + w;
}

function bgmScrollHandler() {
  if (!bgmTrack || !bgmStep) return;
  bgmSetDot(bgmRealIndex());

  // Backstop only: if a fling somehow reaches within one slide of a real track
  // end there is no runway left, so wrap immediately even mid-gesture. A visible
  // stutter beats dead-ending against the edge.
  const maxScroll = bgmTrack.scrollWidth - bgmTrack.clientWidth;
  if (bgmHasLead && bgmTrack.scrollLeft <= bgmStep) { bgmWrap(); return; }
  if (bgmTrack.scrollLeft >= maxScroll - bgmStep) { bgmWrap(); return; }

  if (bgmSettleTimer) window.clearTimeout(bgmSettleTimer);
  bgmSettleTimer = window.setTimeout(() => {
    // First forward move unlocks backward scrolling for the rest of the visit.
    if (!bgmHasLead && bgmTrack && bgmTrack.scrollLeft >= bgmStep) bgmAddLeadCopy();
    bgmWrap();
    bgmReportSwipe();
  }, 150);
}

/* Tell Clarity a gallery swipe happened.

   Fires only once the scroll has settled AND the real slide index actually
   changed. Two reasons that matters: the scroll event fires dozens of times per
   swipe, and the loop repositions scrollLeft programmatically — keying off raw
   position would log a flood of phantom swipes and make the data worthless.

   Clarity is injected outside the theme (admin/app), and only after cookie
   consent, so window.clarity may legitimately never exist. The guard means no
   consent, no Clarity, no error — the gallery just carries on. */
function bgmReportSwipe() {
  if (!bgmRealCount) return;
  const idx = bgmRealIndex();
  if (idx === bgmLastReportedIndex) return; // wrap or settle with no real move
  bgmLastReportedIndex = idx;
  if (typeof window.clarity === 'function') {
    try { window.clarity('event', 'gallery_swipe'); } catch (e) {}
  }
  // Announced for anything else that cares (e.g. the Klaviyo popup trigger in
  // theme.liquid). Same guarantees as the Clarity event: fires once per settled
  // slide change, never mid-gesture, never for the loop's internal wrap.
  try { document.dispatchEvent(new CustomEvent('brume:gallery-swipe')); } catch (e) {}
}

// Tapping a partially-visible neighbour (either side) scrolls it into view.
// Tapping the slide that's already active does nothing.
function bgmTapHandler(e) {
  const slide = e.target.closest ? e.target.closest('.bgm-slide') : null;
  if (!slide || !bgmTrack || !bgmStep) return;
  const slides = bgmSlides();
  const i = slides.indexOf(slide);
  if (i === -1 || i === bgmAbsIndex()) return;
  e.preventDefault();
  // Scroll to this exact element rather than its real-index equivalent, so the
  // movement matches the direction the user tapped.
  const left = i * bgmStep;
  bgmTrack.scrollTo({ left: left, behavior: 'smooth' });
  window.setTimeout(function () {
    if (bgmTrack && Math.abs(bgmTrack.scrollLeft - left) > 2) {
      bgmTrack.scrollTo({ left: left, behavior: 'auto' });
    }
  }, 400);
}

function bgmSetColor(color) {
  if (!bgmTrack) return;
  // Publish the colour on the track so CSS can pick between the two media in the
  // tap-up slide. Needed because the src swap below cannot turn a <video> into
  // an <img>, and the Pearl White version of that slide is a still.
  bgmTrack.setAttribute('data-active-color', color);
  // Swaps every colour-aware image in the gallery, clones included.
  bgmTrack.querySelectorAll('.bgm-media[data-' + color + ']').forEach(function (el) {
    if (el.dataset[color]) el.src = el.dataset[color];
  });
}

function initMobileGallery() {
  if (!bgm) return;
  bgmSetColor(currentColor);
  bgmBuildLoop();
  bgmBuildDots();
  if (bgmTrack) {
    bgmTrack.addEventListener('scroll', bgmScrollHandler, { passive: true });
    bgmTrack.addEventListener('click', bgmTapHandler);
  }
}

/* ─────────── colour switching (called by colour selector) ─────────── */
function switchColor(color) {
  currentColor = color;
  if (isMobile()) {
    bgmSetColor(color);
    bgmResetToHome();
    bgmSetDot(0);
  } else if (track) {
    // Desktop mosaic: show only the selected finish's tiles
    track.setAttribute('data-active-color', color);
  }
}

function initBuyPageSlideshow() {
  slideshow = document.querySelector('.product-slideshow');
  if (slideshow) {
    track = slideshow.querySelector('.slideshow-images');
    if (track) track.setAttribute('data-active-color', currentColor);
  }

  bgm = document.querySelector('.buy-gallery-mobile');
  bgmTrack = document.getElementById('bgmTrack');
  bgmImage = document.getElementById('bgmImage');
  bgmDots = document.getElementById('bgmDots');

  if (isMobile()) initMobileGallery();
  else initDesktop();
}

  window.Brume.switchColor = switchColor;
  window.Brume.initBuyPageSlideshow = initBuyPageSlideshow;
})();
